import { type InjectionKey, type ComputedRef, type Ref } from "vue"

export interface SectionInfo {
  id: string
  title: string
  description?: string
  el: Ref<HTMLElement | null>
  hasError: ComputedRef<boolean>
}

export interface FieldInfo {
  /** Stable unique id for wiring the label, control and error message together. */
  id: string
  name: string
  required: boolean
  error: ComputedRef<string | null>
  touched: Ref<boolean>
  dirty: ComputedRef<boolean>
  sectionId: string | null
  /** Sichtbare Beschriftung (FormField-Label) — fuer Konflikt- und Uebernahme-Hinweise. */
  label?: string
  /** Formularschluessel, dessen Aenderung dieses Feld anzeigt, falls er von `name` abweicht. */
  dirtyField?: string
}

/** Ein Feld, das ich und jemand anderes seit dem Oeffnen unterschiedlich geaendert haben. */
export interface FieldConflict {
  key: string
  label: string
  mine: unknown
  theirs: unknown
}

export type ConflictChoice = "mine" | "theirs"

/** Fragt den Redakteur je Konfliktfeld; null = Speichern abbrechen. */
export type ConflictResolver = (conflicts: FieldConflict[]) => Promise<Record<string, ConflictChoice> | null>

export interface FormApi<T> {
  value: Ref<T>
  isDirty: ComputedRef<boolean>
  changedCount: ComputedRef<number>
  changedFieldNames: ComputedRef<string[]>
  isValid: ComputedRef<boolean>
  isSaving: Ref<boolean>
  isReady: ComputedRef<boolean>
  save(): Promise<void>
  reset(): void
  /**
   * Snapshot the current value as the new dirty-state baseline.
   * Call this right after a successful save when the editor is about to
   * navigate to a different route (e.g. after create → router.push to edit
   * route) so the onBeforeRouteLeave guard does not prompt the user.
   */
  markSaved(): void
  setFieldError(name: string, message: string | null): void
  serverErrors: Ref<Record<string, string | null>>
  formErrors: ComputedRef<Record<string, string>>
  registerSection(s: SectionInfo): void
  unregisterSection(id: string): void
  sections: ComputedRef<SectionInfo[]>
  registerField(f: FieldInfo): void
  unregisterField(name: string): void
  fields: ComputedRef<FieldInfo[]>
  /** Registriert den Dialog, den save() bei Feldkonflikten fragt (null = keiner). */
  setConflictResolver(resolver: ConflictResolver | null): void
}

export const FormContextKey: InjectionKey<FormApi<unknown>> = Symbol("FormContext")
export const SectionIdInjectionKey: InjectionKey<string> = Symbol("FormSectionId")

// Teleport target for popovers/dropdowns rendered by form inputs (combobox,
// async combobox). Defaults to `"body"`. Override inside a modal (e.g.
// `<dialog>` opened via `showModal()`) by providing the dialog element, so
// the popover lands in the dialog's top-layer instead of the body below it.
export const ComboboxPortalTargetKey: InjectionKey<Ref<string | HTMLElement>> =
  Symbol("ComboboxPortalTarget")
